import { FC, useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Button, Descriptions, Drawer, Input, Modal, Space, Table, Tag, message } from "antd";
import { useTranslation } from "react-i18next";
import aiChatAdminService from "src/module/aiChatAdmin/service/aiChatAdmin.service";
import {
	AiConversation,
	AiConversationDetail,
	AiConversationMessage,
} from "src/module/aiChatAdmin/entity/aiChatAdmin.entity";

const PAGE_SIZE = 15;

export const AiConversations: FC = () => {
	const { t } = useTranslation();
	const [list, setList] = useState<AiConversation[]>([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);
	const [keyword, setKeyword] = useState("");
	const [subjectKey, setSubjectKey] = useState("");
	const [page, setPage] = useState(1);
	const [detail, setDetail] = useState<AiConversationDetail>();

	const loadData = useCallback(async () => {
		setLoading(true);
		try {
			const result = await aiChatAdminService.listConversations({
				q: keyword,
				subjectKey: subjectKey || undefined,
				limit: PAGE_SIZE,
				offset: (page - 1) * PAGE_SIZE,
			});
			setList(result.records);
			setTotal(result.total);
		} catch {
			setList([]);
			setTotal(0);
		} finally {
			setLoading(false);
		}
	}, [keyword, page, subjectKey]);

	useEffect(() => {
		void loadData();
	}, [loadData]);

	const openDetail = async (record: AiConversation) => {
		const result = await aiChatAdminService.getConversation(record.id);
		setDetail(result);
	};

	const remove = (record: AiConversation) => {
		Modal.confirm({
			title: "确认删除该聊天记录?",
			onOk: async () => {
				await aiChatAdminService.deleteConversation(record.id);
				message.success(t("operate_success"));
				loadData();
			},
		});
	};

	const purge = () => {
		Modal.confirm({
			title: "确认清理已过期聊天记录?",
			onOk: async () => {
				const result = await aiChatAdminService.purgeExpired();
				message.success(`已清理 ${result.purge.deletedConversations} 条会话`);
				loadData();
			},
		});
	};

	const columns = [
		{ title: "conversationId", dataIndex: "id", width: 300 },
		{ title: "subjectKey", dataIndex: "subject_key", width: 260 },
		{ title: "类型", dataIndex: "actor_type", width: 110 },
		{ title: "用户名", dataIndex: "user_name", width: 140 },
		{ title: t("mobile"), dataIndex: "mobile", width: 130 },
		{ title: "渠道", dataIndex: "channel", width: 120 },
		{ title: "消息数", dataIndex: "message_count", width: 90 },
		{
			title: "状态",
			dataIndex: "blocked",
			width: 90,
			render: (value: number) => value === 1 ? <Tag color="red">已拉黑</Tag> : <Tag color="green">正常</Tag>,
		},
		{ title: "更新时间", dataIndex: "updated_at", width: 190 },
		{ title: "过期时间", dataIndex: "expires_at", width: 190 },
		{
			title: t("action"),
			width: 170,
			fixed: "right" as const,
			render: (_value: unknown, record: AiConversation) => (
				<Space>
					<Button type="primary" onClick={() => openDetail(record)}>查看</Button>
					<Button danger onClick={() => remove(record)}>{t("delete")}</Button>
				</Space>
			),
		},
	];

	return (
		<Container>
			<Header>
				<Space wrap>
					<Input.Search
						allowClear
						placeholder="conversationId / 用户 / 消息内容"
						value={keyword}
						onChange={(event) => setKeyword(event.target.value)}
						onSearch={() => {
							setPage(1);
							loadData();
						}}
						style={{ width: 300 }}
					/>
					<Input.Search
						allowClear
						placeholder="subjectKey 精确查询"
						value={subjectKey}
						onChange={(event) => setSubjectKey(event.target.value)}
						onSearch={() => {
							setPage(1);
							loadData();
						}}
						style={{ width: 300 }}
					/>
					<Button onClick={loadData}>{t("search")}</Button>
					<Button danger onClick={purge}>清理过期记录</Button>
				</Space>
			</Header>
			<Table
				loading={loading}
				columns={columns}
				dataSource={list}
				rowKey={(record) => record.id}
				pagination={false}
				scroll={{ x: 1900 }}
			/>
			<Pager>
				<Button disabled={page <= 1} onClick={() => setPage(page - 1)}>上一页</Button>
				<span>第 {page} 页，共 {total} 条</span>
				<Button disabled={page * PAGE_SIZE >= total} onClick={() => setPage(page + 1)}>下一页</Button>
			</Pager>
			<Drawer
				open={!!detail}
				title="AI 聊天记录详情"
				width={820}
				onClose={() => setDetail(undefined)}
			>
				{detail && (
					<>
						<Descriptions bordered size="small" column={2}>
							<Descriptions.Item label="conversationId" span={2}>{detail.conversation.id}</Descriptions.Item>
							<Descriptions.Item label="subjectKey" span={2}>{detail.conversation.subject_key}</Descriptions.Item>
							<Descriptions.Item label="用户名">{detail.conversation.user_name || "-"}</Descriptions.Item>
							<Descriptions.Item label={t("mobile")}>{detail.conversation.mobile || "-"}</Descriptions.Item>
							<Descriptions.Item label="渠道">{detail.conversation.channel}</Descriptions.Item>
							<Descriptions.Item label="消息数">{detail.conversation.message_count}</Descriptions.Item>
							<Descriptions.Item label="开始时间">{detail.conversation.started_at}</Descriptions.Item>
							<Descriptions.Item label="更新时间">{detail.conversation.updated_at}</Descriptions.Item>
						</Descriptions>
						<MessageList>
							{detail.messages.map((item: AiConversationMessage) => (
								<MessageItem key={item.id} role={item.role}>
									<div className="meta">
										<Tag color={item.role === "user" ? "blue" : "green"}>
											{item.role === "user" ? "用户" : "AI"}
										</Tag>
										<span>{item.created_at}</span>
										<span>{item.message_uid}</span>
									</div>
									<pre>{item.content}</pre>
								</MessageItem>
							))}
						</MessageList>
					</>
				)}
			</Drawer>
		</Container>
	);
};

const Container = styled.div`
	width: 100%;
	padding: 20px;
`;

const Header = styled.div`
	margin-bottom: 16px;
`;

const Pager = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 12px;
	margin-top: 16px;
`;

const MessageList = styled.div`
	margin-top: 20px;
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

const MessageItem = styled.div<{ role: string }>`
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	padding: 12px;
	background: ${(props) => props.role === "user" ? "#f0f7ff" : "#f6ffed"};

	.meta {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 8px;
		color: #666;
	}

	pre {
		margin: 0;
		white-space: pre-wrap;
		word-break: break-word;
		font-family: inherit;
	}
`;
